import React, { useState, useEffect, useMemo } from "react";
import { 
  Building2,
  Users, 
  CalendarDays, 
  Microscope,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Shield,
  Eye,
  ArrowLeft,
  FileText,
  ClipboardList,
  UserCheck,
  Clock,
  AlertCircle,
  Crown,
  Trash2,
  Lock,
  Send,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  User,
  Download,
  Pencil,
  Ticket,
  MessageSquare,
  BarChart3,
  Search,
  Filter,
  X,
  CreditCard,
  Timer,
  Thermometer,
  Bell,
  BellRing
} from "lucide-react";
import UsageThermometer from "../components/UsageThermometer";
import NotificationDemo from "../components/NotificationDemo";
import NotificationSettings from "../components/NotificationSettings";
import FollowupConfig from "../components/FollowupConfig";
import NfseSection from "../components/NfseSection";
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  Legend,
  LineChart,
  Line,
  CartesianGrid
} from "recharts";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { apiRequest, formatDate, formatDateTime } from "../lib/utils";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "../components/ToastProvider";

const StatCard = ({ title, value, icon: Icon, color, description }) => {
  const colorClasses = {
    teal: "bg-teal-50 text-teal-600",
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
    emerald: "bg-emerald-50 text-emerald-600",
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>
              {value}
            </p>
            {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const SuperAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  
  // Modal de detalhes
  const [detalhesOpen, setDetalhesOpen] = useState(false);
  const [detalhesLoading, setDetalhesLoading] = useState(false);
  const [farmaciaDetalhes, setFarmaciaDetalhes] = useState(null);
  
  // Cadastros pendentes
  const [cadastrosPendentes, setCadastrosPendentes] = useState([]);
  const [aprovandoId, setAprovandoId] = useState(null);
  const [planoSelecionado, setPlanoSelecionado] = useState("basico");
  
  // Exclusão de farmácia
  const [excluindoId, setExcluindoId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [senhaConfirmacao, setSenhaConfirmacao] = useState("");
  const [senhaErro, setSenhaErro] = useState("");
  
  // Farmácias expandidas (para mostrar usuários)
  const [expandedFarmacias, setExpandedFarmacias] = useState({});
  
  // Edição de WhatsApp
  const [editingWhatsapp, setEditingWhatsapp] = useState(null);
  const [whatsappValue, setWhatsappValue] = useState("");
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  
  // Tickets
  const [tickets, setTickets] = useState([]);
  const [ticketsStats, setTicketsStats] = useState({});
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketResposta, setTicketResposta] = useState("");
  const [savingTicket, setSavingTicket] = useState(false);
  
  // Conta demo
  const [criandoDemo, setCriandoDemo] = useState(false);
  
  // Demo de notificações push
  const [showNotificationDemo, setShowNotificationDemo] = useState(false);
  
  // Filtros da tabela de farmácias
  const [filtroPlano, setFiltroPlano] = useState("todos");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroBusca, setFiltroBusca] = useState("");

  useEffect(() => {
    checkSuperAdminAccess();
  }, []);

  const checkSuperAdminAccess = async () => {
    try {
      const result = await apiRequest("/super-admin/check");
      setIsSuperAdmin(result.is_super_admin);
      if (result.is_super_admin) {
        fetchAllData();
      }
    } catch (error) {
      console.error("Erro ao verificar acesso:", error);
      setIsSuperAdmin(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsData, usuariosData, pendentesData] = await Promise.all([
        apiRequest("/super-admin/stats"),
        apiRequest("/super-admin/usuarios"),
        apiRequest("/super-admin/cadastros-pendentes"),
      ]);
      setStats(statsData);
      setUsuarios(usuariosData);
      setCadastrosPendentes(pendentesData || []);
      
      // Buscar tickets
      fetchTickets();
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados do super admin");
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async () => {
    try {
      const data = await apiRequest("/super-admin/tickets");
      setTickets(data.tickets || []);
      setTicketsStats(data.stats || {});
    } catch (error) {
      console.error("Erro ao carregar tickets:", error);
    }
  };

  const atualizarTicket = async (ticketId, status, resposta) => {
    setSavingTicket(true);
    try {
      await apiRequest(`/super-admin/tickets/${ticketId}`, {
        method: "PUT",
        body: JSON.stringify({ status, resposta })
      });
      toast.success("Ticket atualizado com sucesso!");
      setSelectedTicket(null);
      setTicketResposta("");
      fetchTickets();
    } catch (error) {
      toast.error("Erro ao atualizar ticket");
    } finally {
      setSavingTicket(false);
    }
  };

  const excluirTicket = async (ticketId) => {
    if (!window.confirm("Tem certeza que deseja excluir este ticket?")) return;
    
    try {
      await apiRequest(`/super-admin/tickets/${ticketId}`, {
        method: "DELETE"
      });
      toast.success("Ticket excluído com sucesso!");
      setSelectedTicket(null);
      fetchTickets();
    } catch (error) {
      toast.error("Erro ao excluir ticket");
    }
  };

  const aprovarFarmacia = async (farmaciaId) => {
    setAprovandoId(farmaciaId);
    try {
      await apiRequest(`/super-admin/aprovar-farmacia/${farmaciaId}?plano=${planoSelecionado}`, {
        method: "POST"
      });
      toast.success("Farmácia aprovada com sucesso!");
      fetchAllData();
    } catch (error) {
      toast.error("Erro ao aprovar farmácia");
    } finally {
      setAprovandoId(null);
    }
  };

  const rejeitarFarmacia = async (farmaciaId) => {
    setAprovandoId(farmaciaId);
    try {
      await apiRequest(`/super-admin/rejeitar-farmacia/${farmaciaId}`, {
        method: "POST"
      });
      toast.success("Farmácia rejeitada");
      fetchAllData();
    } catch (error) {
      toast.error("Erro ao rejeitar farmácia");
    } finally {
      setAprovandoId(null);
    }
  };

  const alterarPlano = async (farmaciaId, novoPlano) => {
    try {
      await apiRequest(`/super-admin/alterar-plano/${farmaciaId}?plano=${novoPlano}`, {
        method: "PUT"
      });
      toast.success(`Plano alterado para ${novoPlano}`);
      fetchAllData();
    } catch (error) {
      toast.error("Erro ao alterar plano");
    }
  };

  const verDetalhesFarmacia = async (farmaciaId) => {
    setDetalhesLoading(true);
    setDetalhesOpen(true);
    try {
      const detalhes = await apiRequest(`/super-admin/farmacias/${farmaciaId}/detalhes`);
      setFarmaciaDetalhes(detalhes);
    } catch (error) {
      console.error("Erro ao carregar detalhes:", error);
      toast.error("Erro ao carregar detalhes da farmácia");
      setDetalhesOpen(false);
    } finally {
      setDetalhesLoading(false);
    }
  };

  const excluirFarmacia = async (farmacia) => {
    // Validar senha antes de excluir
    if (!senhaConfirmacao) {
      setSenhaErro("Digite sua senha para confirmar");
      return;
    }
    
    setExcluindoId(farmacia.id);
    setSenhaErro("");
    
    try {
      // Primeiro, validar a senha do Super Admin
      await apiRequest("/auth/validar-senha", {
        method: "POST",
        body: JSON.stringify({ senha: senhaConfirmacao })
      });
      
      // Se a senha for válida, prosseguir com a exclusão
      const result = await apiRequest(`/super-admin/farmacias/${farmacia.id}`, {
        method: "DELETE"
      });
      toast.success(result.message || "Farmácia excluída com sucesso");
      setConfirmDelete(null);
      setSenhaConfirmacao("");
      fetchAllData();
    } catch (error) {
      console.error("Erro ao excluir farmácia:", error);
      if (error.message?.includes("senha") || error.message?.includes("Senha")) {
        setSenhaErro("Senha incorreta");
      } else {
        toast.error(error.message || "Erro ao excluir farmácia");
      }
    } finally {
      setExcluindoId(null);
    }
  };
  
  const handleCloseDeleteModal = () => {
    setConfirmDelete(null);
    setSenhaConfirmacao("");
    setSenhaErro("");
  };

  // Toggle expandir/colapsar farmácia
  const toggleFarmacia = (farmaciaId) => {
    setExpandedFarmacias(prev => ({
      ...prev,
      [farmaciaId]: !prev[farmaciaId]
    }));
  };

  // Formatação de WhatsApp - apenas DDD + número (sem +55)
  const formatWhatsappInput = (value) => {
    // Remove tudo que não é número
    let numbers = value.replace(/\D/g, '');
    
    // Se começa com 55, remove (o usuário não precisa digitar)
    if (numbers.startsWith('55') && numbers.length > 2) {
      numbers = numbers.slice(2);
    }
    
    // Formata como (XX) XXXXX-XXXX
    if (numbers.length <= 11) {
      let formatted = '';
      if (numbers.length > 0) {
        formatted = '(' + numbers.slice(0, 2);
      }
      if (numbers.length > 2) {
        formatted += ') ' + numbers.slice(2, 7);
      }
      if (numbers.length > 7) {
        formatted += '-' + numbers.slice(7, 11);
      }
      return formatted;
    }
    return value;
  };

  // Abrir edição de WhatsApp
  const handleEditWhatsapp = (user) => {
    setEditingWhatsapp(user);
    // Formatar o whatsapp existente para exibição (sem o 55)
    const currentWhatsapp = user.whatsapp || "";
    setWhatsappValue(formatWhatsappInput(currentWhatsapp));
  };

  // Salvar WhatsApp
  const handleSaveWhatsapp = async () => {
    if (!editingWhatsapp) return;
    
    setSavingWhatsapp(true);
    try {
      // Remover formatação - enviar apenas números
      let numbersOnly = whatsappValue.replace(/\D/g, '');
      
      // Adicionar 55 se não tiver
      if (!numbersOnly.startsWith('55')) {
        numbersOnly = '55' + numbersOnly;
      }
      
      await apiRequest(`/super-admin/usuarios/${editingWhatsapp.id}/whatsapp`, {
        method: "PUT",
        body: JSON.stringify({ whatsapp: numbersOnly })
      });
      
      toast.success("WhatsApp atualizado com sucesso!");
      setEditingWhatsapp(null);
      setWhatsappValue("");
      await fetchAllData();
    } catch (error) {
      toast.error(error.message || "Erro ao atualizar WhatsApp");
    } finally {
      setSavingWhatsapp(false);
    }
  };

  const criarContaDemo = async () => {
    setCriandoDemo(true);
    try {
      const result = await apiRequest("/super-admin/criar-conta-demo", {
        method: "POST"
      });
      toast.success("Conta demo criada com sucesso!");
      toast.success(`Email: demo@conformearesolucao.com.br | Senha: Demo@2025`);
      // Recarregar dados
      await fetchAllData();
    } catch (error) {
      toast.error(error.message || "Erro ao criar conta demo");
    } finally {
      setCriandoDemo(false);
    }
  };

  // Exportar contatos para CSV
  const exportarContatos = () => {
    if (!usuarios || usuarios.length === 0) {
      toast.error("Não há contatos para exportar");
      return;
    }

    // Criar dados para CSV
    const headers = ["Nome", "Email", "WhatsApp", "Farmácia", "CNPJ", "Plano", "Tipo", "Data Cadastro"];
    
    const rows = usuarios.map(user => {
      // Encontrar farmácia do usuário
      const farmacia = stats?.farmacias?.find(f => f.id === user.farmacia_id);
      
      // Formatar WhatsApp para exibição
      const whatsappFormatado = user.whatsapp 
        ? `+${user.whatsapp.slice(0,2)} (${user.whatsapp.slice(2,4)}) ${user.whatsapp.slice(4,9)}-${user.whatsapp.slice(9)}`
        : "";
      
      return [
        user.nome || "",
        user.email || "",
        whatsappFormatado,
        user.farmacia_nome || "",
        user.farmacia_cnpj || "",
        farmacia?.plano || "basico",
        user.role === "admin" ? "Administrador" : "Usuário",
        user.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : ""
      ];
    });

    // Criar conteúdo CSV
    const csvContent = [
      headers.join(";"),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(";"))
    ].join("\n");

    // Criar blob e download
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `contatos_farmacias_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`${usuarios.length} contatos exportados com sucesso!`);
  };

  // Lógica de filtragem das farmácias
  const farmaciasFiltradas = useMemo(() => {
    if (!stats?.farmacias) return [];
    
    return stats.farmacias.filter(f => {
      // Excluir pendentes (já mostrados em seção separada)
      if (f.status === "pendente") return false;
      
      // Filtro por plano
      if (filtroPlano !== "todos" && f.plano !== filtroPlano) return false;
      
      // Filtro por status
      if (filtroStatus !== "todos") {
        if (filtroStatus === "ativo" && f.status !== "aprovado") return false;
        if (filtroStatus === "inativo" && f.status === "aprovado") return false;
      }
      
      // Filtro por busca (nome ou CNPJ)
      if (filtroBusca) {
        const busca = filtroBusca.toLowerCase();
        const nomeMatch = (f.nome_fantasia || f.nome || "").toLowerCase().includes(busca);
        const cnpjMatch = (f.cnpj || "").replace(/\D/g, "").includes(busca.replace(/\D/g, ""));
        if (!nomeMatch && !cnpjMatch) return false;
      }
      
      return true;
    });
  }, [stats?.farmacias, filtroPlano, filtroStatus, filtroBusca]);

  // Limpar todos os filtros
  const limparFiltros = () => {
    setFiltroPlano("todos");
    setFiltroStatus("todos");
    setFiltroBusca("");
  };

  // Verificar se há filtros ativos
  const temFiltrosAtivos = filtroPlano !== "todos" || filtroStatus !== "todos" || filtroBusca !== "";

  // Função para calcular status de assinatura da farmácia
  const getSubscriptionInfo = (farm) => {
    const hasPaid = farm.has_paid === true || farm.subscription_status === "active";
    const trialStartDate = farm.trial_start_date;
    const trialEndDate = farm.trial_end_date;
    const isPermanentTrial = farm.is_permanent_trial === true;
    
    // Verificar se é farmácia de demonstração (CNPJ demo ou flag)
    const cnpjClean = (farm.cnpj || '').replace(/\D/g, '');
    const isDemo = isPermanentTrial || cnpjClean === '00000000000191';
    
    // Se é demo, mostrar badge especial
    if (isDemo) {
      return {
        status: 'demo',
        label: 'Demo Permanente',
        ciclo: '-',
        daysRemaining: 999,
        color: 'bg-purple-600 text-white border-purple-700'
      };
    }
    
    // Se já pagou
    if (hasPaid) {
      // Mapear ciclos de pagamento
      const cicloMap = {
        'monthly': 'Mensal',
        'quarterly': 'Trimestral',
        'semi_annual': 'Semestral',
        'annual': 'Anual'
      };
      return {
        status: 'paid',
        label: 'Assinante',
        ciclo: cicloMap[farm.billing_cycle] || farm.billing_cycle || 'N/A',
        color: 'bg-emerald-100 text-emerald-800 border-emerald-200'
      };
    }
    
    // Se está em trial
    if (trialStartDate) {
      const now = new Date();
      const endDate = new Date(trialEndDate);
      const daysRemaining = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
      
      // Cores baseadas nos dias restantes
      let color;
      if (daysRemaining <= 0) {
        // Expirado - vermelho escuro
        color = 'bg-red-600 text-white border-red-700';
      } else if (daysRemaining === 1) {
        // 1 dia - vermelho
        color = 'bg-red-500 text-white border-red-600';
      } else if (daysRemaining === 2) {
        // 2 dias - vermelho claro
        color = 'bg-red-400 text-white border-red-500';
      } else if (daysRemaining === 3) {
        // 3 dias - laranja escuro
        color = 'bg-orange-500 text-white border-orange-600';
      } else if (daysRemaining === 4) {
        // 4 dias - laranja
        color = 'bg-orange-400 text-white border-orange-500';
      } else if (daysRemaining === 5) {
        // 5 dias - amarelo escuro
        color = 'bg-amber-500 text-white border-amber-600';
      } else if (daysRemaining === 6) {
        // 6 dias - amarelo/verde
        color = 'bg-lime-500 text-white border-lime-600';
      } else {
        // 7+ dias - verde
        color = 'bg-emerald-500 text-white border-emerald-600';
      }
      
      if (daysRemaining <= 0) {
        return {
          status: 'expired',
          label: 'Trial Expirado',
          ciclo: '-',
          daysRemaining: 0,
          color: color
        };
      }
      
      return {
        status: 'trial',
        label: `Trial (${daysRemaining}d)`,
        ciclo: '-',
        daysRemaining,
        color: color
      };
    }
    
    // Sem informação de trial (farmácia antiga)
    return {
      status: 'unknown',
      label: 'Indefinido',
      ciclo: '-',
      color: 'bg-slate-100 text-slate-600 border-slate-200'
    };
  };

  if (checkingAccess) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </Layout>
    );
  }

  if (!isSuperAdmin) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Acesso Restrito
          </h1>
          <p className="text-slate-500 max-w-md">
            Esta área e exclusiva para administradores do sistema Conforme a Resolução.
            Se você deveria ter acesso, entre em contato com o suporte.
          </p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-slide-in" data-testid="super-admin-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Super Admin
              </h1>
              <p className="text-slate-500">Painel de gestão do SaaS</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={exportarContatos}
              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              title="Exportar lista de contatos para CSV"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar Contatos
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowNotificationDemo(true)}
              className="text-purple-600 border-purple-200 hover:bg-purple-50"
              title="Preview de Notificações Push"
            >
              <Bell className="w-4 h-4 mr-2" />
              Preview Push
            </Button>
            <Button 
              variant="outline" 
              onClick={criarContaDemo} 
              disabled={criandoDemo}
              className="text-teal-600 border-teal-200 hover:bg-teal-50"
            >
              {criandoDemo ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserCheck className="w-4 h-4 mr-2" />
              )}
              Criar Conta Demo
            </Button>
            <Button variant="outline" onClick={fetchAllData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </div>

        {/* Modal de Demonstração de Notificações */}
        {showNotificationDemo && (
          <NotificationDemo onClose={() => setShowNotificationDemo(false)} />
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Farmácias Ativas"
            value={stats?.total_farmacias || 0}
            icon={Building2}
            color="purple"
            description="Total de clientes"
          />
          <StatCard
            title="Total de Usuários"
            value={stats?.total_usuarios || 0}
            icon={Users}
            color="blue"
            description="Em todas as farmácias"
          />
          <StatCard
            title="Documentos e Alvarás"
            value={stats?.total_eventos || 0}
            icon={FileText}
            color="teal"
            description="Documentos gerenciados"
          />
          <StatCard
            title="Itens Calibráveis"
            value={stats?.total_itens || 0}
            icon={Microscope}
            color="emerald"
            description="Equipamentos cadastrados"
          />
        </div>

        {/* Stats Secundários */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            title="Funcionários"
            value={stats?.total_funcionarios || 0}
            icon={UserCheck}
            color="blue"
            description="Total cadastrado"
          />
          <StatCard
            title="Verificações Diárias"
            value={stats?.total_verificacoes || 0}
            icon={ClipboardList}
            color="teal"
            description="Registros realizados"
          />
          <StatCard
            title="Pendentes"
            value={cadastrosPendentes.length}
            icon={Clock}
            color="amber"
            description="Aguardando aprovação"
          />
        </div>

        {/* Cadastros Pendentes - Alerta */}
        {cadastrosPendentes.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <AlertCircle className="w-5 h-5" />
                Cadastros Aguardando Aprovacao ({cadastrosPendentes.length})
              </CardTitle>
              <CardDescription className="text-amber-700">
                Novos clientes aguardando liberacao de acesso
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {cadastrosPendentes.map((farm) => (
                  <div 
                    key={farm.id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white rounded-lg border border-amber-200 gap-4"
                  >
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900">{farm.nome_fantasia || farm.nome}</h4>
                      <p className="text-sm text-slate-500">{farm.razao_social}</p>
                      <div className="flex flex-wrap gap-2 mt-1 text-xs text-slate-500">
                        <span>CNPJ: {farm.cnpj || "-"}</span>
                        <span>•</span>
                        <span>Admin: {farm.admin_nome}</span>
                        <span>•</span>
                        <span>{farm.admin_email}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Select 
                        value={planoSelecionado} 
                        onValueChange={setPlanoSelecionado}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Plano" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="basico">Basico (2 usuarios)</SelectItem>
                          <SelectItem value="profissional">Profissional (6)</SelectItem>
                          <SelectItem value="enterprise">Enterprise (11)</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => aprovarFarmacia(farm.id)}
                        disabled={aprovandoId === farm.id}
                      >
                        {aprovandoId === farm.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Aprovar
                          </>
                        )}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => rejeitarFarmacia(farm.id)}
                        disabled={aprovandoId === farm.id}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Rejeitar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Modal de confirmação de exclusão com senha */}
        <Dialog open={confirmDelete !== null} onOpenChange={(open) => !open && handleCloseDeleteModal()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Excluir Farmácia
              </DialogTitle>
              <DialogDescription>
                Tem certeza que deseja excluir a farmácia <strong>{confirmDelete?.nome_fantasia || confirmDelete?.nome}</strong>?
                <br /><br />
                <span className="text-red-500 font-semibold">
                  Esta ação é irreversível e excluirá todos os dados da farmácia:
                </span>
                <ul className="list-disc list-inside mt-2 text-sm text-slate-600">
                  <li>Todos os documentos e alvarás</li>
                  <li>Todos os eventos</li>
                  <li>Todos os funcionários</li>
                  <li>Todos os itens calibráveis</li>
                  <li>Todas as verificações</li>
                  <li>Todos os usuários da farmácia</li>
                </ul>
              </DialogDescription>
            </DialogHeader>
            
            {/* Campo de senha para confirmação */}
            <div className="space-y-2 mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <Label htmlFor="senha-confirmacao" className="flex items-center gap-2 text-slate-700">
                <Lock className="w-4 h-4" />
                Digite sua senha para confirmar
              </Label>
              <Input
                id="senha-confirmacao"
                type="password"
                value={senhaConfirmacao}
                onChange={(e) => {
                  setSenhaConfirmacao(e.target.value);
                  setSenhaErro("");
                }}
                placeholder="Sua senha de Super Admin"
                className={senhaErro ? "border-red-500" : ""}
              />
              {senhaErro && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {senhaErro}
                </p>
              )}
            </div>
            
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={handleCloseDeleteModal}>
                Cancelar
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => excluirFarmacia(confirmDelete)}
                disabled={excluindoId === confirmDelete?.id || !senhaConfirmacao}
              >
                {excluindoId === confirmDelete?.id ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Excluir Permanentemente
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Lista de Farmácias com Accordion */}
        <Card>
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Farmácias Cadastradas
                  {temFiltrosAtivos && (
                    <Badge variant="secondary" className="ml-2">
                      {farmaciasFiltradas.length} resultado{farmaciasFiltradas.length !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Clique na seta para ver os usuários de cada farmácia
                </CardDescription>
              </div>
              
              {/* Filtros */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Busca */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nome ou CNPJ..."
                    value={filtroBusca}
                    onChange={(e) => setFiltroBusca(e.target.value)}
                    className="pl-9 w-[200px] h-9"
                    data-testid="filter-search"
                  />
                </div>
                
                {/* Filtro por Plano */}
                <Select value={filtroPlano} onValueChange={setFiltroPlano}>
                  <SelectTrigger className="w-[130px] h-9" data-testid="filter-plano">
                    <Filter className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Plano" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os Planos</SelectItem>
                    <SelectItem value="basico">Básico</SelectItem>
                    <SelectItem value="profissional">Profissional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Filtro por Status */}
                <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                  <SelectTrigger className="w-[120px] h-9" data-testid="filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="ativo">Ativos</SelectItem>
                    <SelectItem value="inativo">Inativos</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Botão Limpar Filtros */}
                {temFiltrosAtivos && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={limparFiltros}
                    className="h-9 text-slate-500 hover:text-slate-700"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Limpar
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {farmaciasFiltradas.length === 0 ? (
              <div className="text-center py-8">
                {temFiltrosAtivos ? (
                  <div className="space-y-2">
                    <Filter className="w-12 h-12 mx-auto text-slate-300" />
                    <p className="text-slate-500">Nenhuma farmácia encontrada com os filtros selecionados</p>
                    <Button variant="link" onClick={limparFiltros} className="text-teal-600">
                      Limpar filtros
                    </Button>
                  </div>
                ) : (
                  <p className="text-slate-500">Nenhuma farmácia cadastrada</p>
                )}
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Farmácia</TableHead>
                      <TableHead>CNPJ</TableHead>
                      <TableHead className="text-center">Assinatura</TableHead>
                      <TableHead className="text-center">Ciclo</TableHead>
                      <TableHead className="text-center">Uso</TableHead>
                      <TableHead className="text-center">Plano</TableHead>
                      <TableHead className="text-center">Usuários</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {farmaciasFiltradas.map((farm) => {
                      const usuariosFarmacia = usuarios.filter(u => u.farmacia_id === farm.id);
                      const isExpanded = expandedFarmacias[farm.id];
                      const subscriptionInfo = getSubscriptionInfo(farm);
                      
                      return (
                        <React.Fragment key={farm.id}>
                          {/* Linha da Farmácia */}
                          <TableRow className="hover:bg-slate-50">
                            <TableCell className="w-10">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 h-8 w-8"
                                onClick={() => toggleFarmacia(farm.id)}
                              >
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-slate-500" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-slate-500" />
                                )}
                              </Button>
                            </TableCell>
                            <TableCell>
                              <p className="font-medium">{farm.nome_fantasia || farm.nome}</p>
                            </TableCell>
                            <TableCell className="font-mono text-sm text-slate-600">
                              {farm.cnpj || "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={`${subscriptionInfo.color} border`}>
                                {subscriptionInfo.status === 'trial' && (
                                  <Timer className="w-3 h-3 mr-1" />
                                )}
                                {subscriptionInfo.status === 'paid' && (
                                  <CreditCard className="w-3 h-3 mr-1" />
                                )}
                                {subscriptionInfo.status === 'expired' && (
                                  <XCircle className="w-3 h-3 mr-1" />
                                )}
                                {subscriptionInfo.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="text-sm text-slate-600">
                                {subscriptionInfo.ciclo}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <UsageThermometer farmaciaId={farm.id} compact />
                            </TableCell>
                            <TableCell className="text-center">
                              <Select 
                                value={farm.plano || "basico"} 
                                onValueChange={(value) => alterarPlano(farm.id, value)}
                              >
                                <SelectTrigger className="w-[110px] h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="basico">Básico</SelectItem>
                                  <SelectItem value="profissional">Profissional</SelectItem>
                                  <SelectItem value="enterprise">Enterprise</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className={
                                farm.total_usuarios >= (farm.max_usuarios || 2) 
                                  ? "bg-red-50 text-red-700 border-red-200" 
                                  : ""
                              }>
                                {farm.total_usuarios}/{farm.max_usuarios || 2}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {farm.status === "aprovado" ? (
                                <Badge className="bg-emerald-100 text-emerald-800">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Ativo
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-800">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Inativo
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex gap-1 justify-center">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => verDetalhesFarmacia(farm.id)}
                                  className="text-purple-600 hover:bg-purple-50 h-8 px-2"
                                  title="Ver detalhes"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => setConfirmDelete(farm)}
                                  className="text-red-600 hover:bg-red-50 h-8 px-2"
                                  title="Excluir"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          
                          {/* Linha Expandida - Usuários */}
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={10} className="p-0 bg-slate-50">
                                <div className="p-4 border-t">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Users className="w-4 h-4 text-slate-500" />
                                    <span className="text-sm font-medium text-slate-700">
                                      Usuários ({usuariosFarmacia.length})
                                    </span>
                                  </div>
                                  {usuariosFarmacia.length === 0 ? (
                                    <p className="text-sm text-slate-400 italic">Nenhum usuário cadastrado</p>
                                  ) : (
                                    <div className="bg-white rounded-lg border divide-y">
                                      {usuariosFarmacia.map((user) => (
                                        <div key={user.id} className="px-4 py-2 flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                            {user.role === 'admin' ? (
                                              <Crown className="w-4 h-4 text-amber-500" />
                                            ) : (
                                              <User className="w-4 h-4 text-slate-400" />
                                            )}
                                            <div>
                                              <div className="flex items-center gap-2">
                                                <span className="font-medium text-sm">{user.nome}</span>
                                                {user.role === 'admin' && (
                                                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-600 border-amber-200 px-1 py-0">
                                                    Admin
                                                  </Badge>
                                                )}
                                              </div>
                                              <div className="flex items-center gap-3 text-xs text-slate-500">
                                                <span>{user.email}</span>
                                                {user.role === 'admin' && (
                                                  user.whatsapp ? (
                                                    <span className="flex items-center gap-1 text-emerald-600">
                                                      <Phone className="w-3 h-3" />
                                                      +{user.whatsapp.slice(0,2)} ({user.whatsapp.slice(2,4)}) {user.whatsapp.slice(4,9)}-{user.whatsapp.slice(9)}
                                                    </span>
                                                  ) : (
                                                    <span className="text-amber-500 italic">Sem WhatsApp</span>
                                                  )
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            {user.role === 'admin' && (
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleEditWhatsapp(user)}
                                                className="h-7 px-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50"
                                                title="Editar WhatsApp"
                                              >
                                                <Pencil className="w-3 h-3" />
                                              </Button>
                                            )}
                                            <span className="text-xs text-slate-400">{formatDate(user.created_at)}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Seção de Tickets/Suporte */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="w-5 h-5" />
                  Tickets de Suporte
                </CardTitle>
                <CardDescription>
                  Gerencie as solicitações de suporte dos clientes
                </CardDescription>
              </div>
              {ticketsStats.tempo_medio_resolucao_horas > 0 && (
                <div className="text-right">
                  <p className="text-sm text-slate-500">Tempo médio de resolução</p>
                  <p className="text-lg font-bold text-teal-600">
                    {ticketsStats.tempo_medio_resolucao_horas < 24 
                      ? `${ticketsStats.tempo_medio_resolucao_horas}h`
                      : `${Math.round(ticketsStats.tempo_medio_resolucao_horas / 24)} dias`
                    }
                  </p>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Stats de Tickets */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold">{ticketsStats.total || 0}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">{ticketsStats.abertos || 0}</p>
                <p className="text-xs text-amber-600">Abertos</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-600">{ticketsStats.em_andamento || 0}</p>
                <p className="text-xs text-blue-600">Em Andamento</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600">{ticketsStats.resolvidos || 0}</p>
                <p className="text-xs text-emerald-600">Resolvidos</p>
              </div>
            </div>

            {/* Gráficos de Tickets */}
            {tickets.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Gráfico de Pizza - Status */}
                <div className="bg-white border rounded-lg p-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Por Status
                  </h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Abertos', value: ticketsStats.abertos || 0, color: '#f59e0b' },
                            { name: 'Em Andamento', value: ticketsStats.em_andamento || 0, color: '#3b82f6' },
                            { name: 'Resolvidos', value: ticketsStats.resolvidos || 0, color: '#10b981' },
                          ].filter(item => item.value > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {[
                            { name: 'Abertos', value: ticketsStats.abertos || 0, color: '#f59e0b' },
                            { name: 'Em Andamento', value: ticketsStats.em_andamento || 0, color: '#3b82f6' },
                            { name: 'Resolvidos', value: ticketsStats.resolvidos || 0, color: '#10b981' },
                          ].filter(item => item.value > 0).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name) => [`${value} tickets`, name]}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Legend 
                          verticalAlign="bottom" 
                          height={36}
                          formatter={(value) => <span className="text-xs text-slate-600">{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Gráfico de Barras - Por Tipo */}
                <div className="bg-white border rounded-lg p-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Por Tipo
                  </h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={(() => {
                          const tipos = tickets.reduce((acc, t) => {
                            const tipo = t.tipo || 'outro';
                            acc[tipo] = (acc[tipo] || 0) + 1;
                            return acc;
                          }, {});
                          return [
                            { name: 'Problema', value: tipos.problema || 0, fill: '#ef4444' },
                            { name: 'Melhoria', value: tipos.melhoria || 0, fill: '#8b5cf6' },
                            { name: 'Dúvida', value: tipos.duvida || 0, fill: '#3b82f6' },
                          ].filter(item => item.value > 0);
                        })()}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis dataKey="name" type="category" width={70} tick={{ fontSize: 12 }} />
                        <Tooltip 
                          formatter={(value) => [`${value} tickets`]}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                          {(() => {
                            const tipos = tickets.reduce((acc, t) => {
                              const tipo = t.tipo || 'outro';
                              acc[tipo] = (acc[tipo] || 0) + 1;
                              return acc;
                            }, {});
                            return [
                              { name: 'Problema', value: tipos.problema || 0, fill: '#ef4444' },
                              { name: 'Melhoria', value: tipos.melhoria || 0, fill: '#8b5cf6' },
                              { name: 'Dúvida', value: tipos.duvida || 0, fill: '#3b82f6' },
                            ].filter(item => item.value > 0).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ));
                          })()}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Gráfico de Linha - Timeline */}
                <div className="bg-white border rounded-lg p-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Últimos 7 dias
                  </h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={(() => {
                          const last7Days = [];
                          for (let i = 6; i >= 0; i--) {
                            const date = new Date();
                            date.setDate(date.getDate() - i);
                            const dateStr = date.toISOString().split('T')[0];
                            const dayName = date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
                            
                            const ticketsNoDia = tickets.filter(t => {
                              const ticketDate = t.created_at?.split('T')[0];
                              return ticketDate === dateStr;
                            }).length;
                            
                            last7Days.push({
                              name: dayName,
                              tickets: ticketsNoDia
                            });
                          }
                          return last7Days;
                        })()}
                        margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip 
                          formatter={(value) => [`${value} tickets`, 'Novos']}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="tickets" 
                          stroke="#0d9488" 
                          strokeWidth={2}
                          dot={{ fill: '#0d9488', strokeWidth: 2 }}
                          activeDot={{ r: 6, fill: '#0d9488' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Lista de Tickets */}
            {tickets.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Nenhum ticket registrado</p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Número</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Título</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-center">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tickets.map((ticket) => (
                      <TableRow key={ticket.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-xs">{ticket.numero}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{ticket.farmacia_nome}</p>
                            <p className="text-xs text-slate-500">{ticket.user_nome}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            ticket.tipo === 'problema' ? 'bg-red-50 text-red-700 border-red-200' :
                            ticket.tipo === 'melhoria' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                            ticket.tipo === 'outros' ? 'bg-green-50 text-green-700 border-green-200' :
                            'bg-blue-50 text-blue-700 border-blue-200'
                          }>
                            {ticket.tipo === 'problema' ? 'Problema' : 
                             ticket.tipo === 'melhoria' ? 'Melhoria' : 
                             ticket.tipo === 'outros' ? 'Outros' : 'Dúvida'}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={ticket.titulo}>
                          {ticket.titulo}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            ticket.status === 'aberto' ? 'bg-amber-100 text-amber-800' :
                            ticket.status === 'em_andamento' ? 'bg-blue-100 text-blue-800' :
                            'bg-emerald-100 text-emerald-800'
                          }>
                            {ticket.status === 'aberto' ? 'Aberto' : 
                             ticket.status === 'em_andamento' ? 'Em Andamento' : 'Resolvido'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {formatDate(ticket.created_at)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedTicket(ticket);
                                setTicketResposta(ticket.resposta || "");
                              }}
                              className="text-teal-600 hover:bg-teal-50"
                              title="Ver detalhes"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => excluirTicket(ticket.id)}
                              className="text-red-600 hover:bg-red-50"
                              title="Excluir ticket"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Configurações de Notificações Push - Super Admin */}
        <NotificationSettings isSuperAdmin={true} />

        {/* Follow-up de Clientes */}
        <FollowupConfig />
        
        {/* Notas Fiscais (NFS-e) */}
        <NfseSection />

        {/* Modal de Detalhes do Ticket */}
        <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-teal-600" />
                Ticket {selectedTicket?.numero}
              </DialogTitle>
              <DialogDescription>
                {selectedTicket?.farmacia_nome} - {selectedTicket?.user_nome} ({selectedTicket?.user_email})
              </DialogDescription>
            </DialogHeader>
            
            {selectedTicket && (
              <div className="space-y-4">
                <Badge variant="outline" className={
                  selectedTicket.tipo === 'problema' ? 'bg-red-50 text-red-700' :
                  selectedTicket.tipo === 'melhoria' ? 'bg-purple-50 text-purple-700' :
                  'bg-blue-50 text-blue-700'
                }>
                  {selectedTicket.tipo === 'problema' ? '🔴 Problema' : 
                   selectedTicket.tipo === 'melhoria' ? '🟣 Melhoria' : '🔵 Dúvida'}
                </Badge>

                <div>
                  <h4 className="font-medium mb-1">{selectedTicket.titulo}</h4>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">
                    {selectedTicket.descricao}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Resposta ao Cliente</Label>
                  <Textarea
                    value={ticketResposta}
                    onChange={(e) => setTicketResposta(e.target.value)}
                    placeholder="Escreva sua resposta..."
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Alterar Status</Label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={selectedTicket.status === 'em_andamento' ? 'default' : 'outline'}
                      onClick={() => atualizarTicket(selectedTicket.id, 'em_andamento', ticketResposta)}
                      disabled={savingTicket}
                      className={selectedTicket.status === 'em_andamento' ? 'bg-blue-600' : ''}
                    >
                      Em Andamento
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedTicket.status === 'resolvido' ? 'default' : 'outline'}
                      onClick={() => atualizarTicket(selectedTicket.id, 'resolvido', ticketResposta)}
                      disabled={savingTicket}
                      className={selectedTicket.status === 'resolvido' ? 'bg-emerald-600' : ''}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Marcar Resolvido
                    </Button>
                  </div>
                </div>

                {selectedTicket.resolved_at && (
                  <p className="text-xs text-slate-400">
                    Resolvido em {formatDate(selectedTicket.resolved_at)}
                  </p>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedTicket(null)}>
                Fechar
              </Button>
              <Button 
                onClick={() => atualizarTicket(selectedTicket.id, selectedTicket.status, ticketResposta)}
                disabled={savingTicket}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {savingTicket ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                Salvar Resposta
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Detalhes da Farmácia */}
        <Dialog open={detalhesOpen} onOpenChange={setDetalhesOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {detalhesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
              </div>
            ) : farmaciaDetalhes ? (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-xl">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    {farmaciaDetalhes.farmacia?.nome_fantasia || farmaciaDetalhes.farmacia?.nome}
                  </DialogTitle>
                  <DialogDescription>
                    CNPJ: {farmaciaDetalhes.farmacia?.cnpj} • {farmaciaDetalhes.farmacia?.endereco}
                  </DialogDescription>
                </DialogHeader>

                {/* Stats da Farmácia */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 my-4">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-blue-600">{farmaciaDetalhes.totais?.usuarios || 0}</p>
                    <p className="text-xs text-blue-600">Usuários</p>
                  </div>
                  <div className="p-3 bg-teal-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-teal-600">{farmaciaDetalhes.totais?.eventos || 0}</p>
                    <p className="text-xs text-teal-600">Documentos</p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-emerald-600">{farmaciaDetalhes.totais?.itens || 0}</p>
                    <p className="text-xs text-emerald-600">Itens</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-purple-600">{farmaciaDetalhes.totais?.verificacoes || 0}</p>
                    <p className="text-xs text-purple-600">Verificações</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-orange-600">{farmaciaDetalhes.totais?.envios || 0}</p>
                    <p className="text-xs text-orange-600">Envios</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-xl font-bold text-amber-600">{farmaciaDetalhes.totais?.funcionarios || 0}</p>
                    <p className="text-xs text-amber-600">Funcionários</p>
                  </div>
                </div>

                {/* Tabs com conteúdo */}
                <Tabs defaultValue="eventos" className="mt-4">
                  <TabsList className="grid w-full grid-cols-3 md:grid-cols-6">
                    <TabsTrigger value="eventos" className="text-xs">
                      <CalendarDays className="w-4 h-4 mr-1 hidden sm:block" />
                      Docs
                    </TabsTrigger>
                    <TabsTrigger value="itens" className="text-xs">
                      <Microscope className="w-4 h-4 mr-1 hidden sm:block" />
                      Itens
                    </TabsTrigger>
                    <TabsTrigger value="verificacoes" className="text-xs">
                      <ClipboardList className="w-4 h-4 mr-1 hidden sm:block" />
                      Verif.
                    </TabsTrigger>
                    <TabsTrigger value="envios" className="text-xs">
                      <Send className="w-4 h-4 mr-1 hidden sm:block" />
                      Envios
                    </TabsTrigger>
                    <TabsTrigger value="funcionarios" className="text-xs">
                      <UserCheck className="w-4 h-4 mr-1 hidden sm:block" />
                      Func.
                    </TabsTrigger>
                    <TabsTrigger value="usuarios" className="text-xs">
                      <Users className="w-4 h-4 mr-1 hidden sm:block" />
                      Usuários
                    </TabsTrigger>
                  </TabsList>

                  {/* Documentos/Eventos */}
                  <TabsContent value="eventos" className="mt-4">
                    {farmaciaDetalhes.eventos?.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Nenhum documento cadastrado</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nome</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Vencimento</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {farmaciaDetalhes.eventos?.map((evento) => (
                            <TableRow key={evento.id}>
                              <TableCell className="font-medium">{evento.nome_evento}</TableCell>
                              <TableCell>{evento.tipo_evento}</TableCell>
                              <TableCell>{formatDate(evento.data_proximo_vencimento)}</TableCell>
                              <TableCell>
                                <Badge className={
                                  evento.status === "em_dia" ? "bg-emerald-100 text-emerald-800" :
                                  evento.status === "vencendo" ? "bg-amber-100 text-amber-800" :
                                  "bg-red-100 text-red-800"
                                }>
                                  {evento.status === "em_dia" ? "Em dia" : 
                                   evento.status === "vencendo" ? "Vencendo" : "Vencido"}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabsContent>

                  {/* Itens */}
                  <TabsContent value="itens" className="mt-4">
                    {farmaciaDetalhes.itens?.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Nenhum item cadastrado</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nome</TableHead>
                            <TableHead>Identificação</TableHead>
                            <TableHead>Categoria</TableHead>
                            <TableHead>Setor</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {farmaciaDetalhes.itens?.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell className="font-medium">{item.nome_item}</TableCell>
                              <TableCell>{item.identificacao_interna || "-"}</TableCell>
                              <TableCell>{item.categoria_nome || item.categoria || "-"}</TableCell>
                              <TableCell>{item.setor_nome || item.setor || "-"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabsContent>

                  {/* Funcionários */}
                  <TabsContent value="funcionarios" className="mt-4">
                    {farmaciaDetalhes.funcionarios?.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Nenhum funcionário cadastrado</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nome</TableHead>
                            <TableHead>Cargo</TableHead>
                            <TableHead>CPF</TableHead>
                            <TableHead>Admissao</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {farmaciaDetalhes.funcionarios?.map((func) => (
                            <TableRow key={func.id}>
                              <TableCell className="font-medium">{func.nome}</TableCell>
                              <TableCell>{func.cargo}</TableCell>
                              <TableCell>{func.cpf || "-"}</TableCell>
                              <TableCell>{formatDate(func.data_admissao)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabsContent>

                  {/* Verificações */}
                  <TabsContent value="verificacoes" className="mt-4">
                    {farmaciaDetalhes.verificacoes?.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Nenhuma verificação realizada</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Item</TableHead>
                            <TableHead>Data</TableHead>
                            <TableHead>Responsável</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {farmaciaDetalhes.verificacoes?.slice(0, 20).map((ver) => (
                            <TableRow key={ver.id}>
                              <TableCell className="font-medium">{ver.tipo}</TableCell>
                              <TableCell>{ver.item_nome || "-"}</TableCell>
                              <TableCell>{formatDateTime(ver.data_verificacao)}</TableCell>
                              <TableCell>{ver.responsavel}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabsContent>

                  {/* Envios */}
                  <TabsContent value="envios" className="mt-4">
                    {farmaciaDetalhes.envios?.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Nenhum envio cadastrado</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nome</TableHead>
                            <TableHead>Recorrência</TableHead>
                            <TableHead>Dia</TableHead>
                            <TableHead>Último Envio</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {farmaciaDetalhes.envios?.map((envio) => (
                            <TableRow key={envio.id}>
                              <TableCell className="font-medium">{envio.nome}</TableCell>
                              <TableCell className="capitalize">{envio.recorrencia || "mensal"}</TableCell>
                              <TableCell>Dia {envio.dia_envio}</TableCell>
                              <TableCell>{envio.ultimo_envio ? formatDate(envio.ultimo_envio) : "Nunca"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabsContent>

                  {/* Usuários */}
                  <TabsContent value="usuarios" className="mt-4">
                    {farmaciaDetalhes.usuarios?.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Nenhum usuário cadastrado</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nome</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>CPF</TableHead>
                            <TableHead>Cadastro</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {farmaciaDetalhes.usuarios?.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell className="font-medium">{user.nome}</TableCell>
                              <TableCell>{user.email}</TableCell>
                              <TableCell>{user.cpf || "-"}</TableCell>
                              <TableCell>{formatDate(user.created_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabsContent>
                </Tabs>
              </>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Metricas futuras */}
        <Card className="border-dashed border-2">
          <CardContent className="p-8 text-center">
            <TrendingUp className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              Metricas e Relatórios
            </h3>
            <p className="text-slate-500 max-w-md mx-auto">
              Em breve: graficos de crescimento, metricas de uso, relatórios de assinaturas e muito mais.
            </p>
          </CardContent>
        </Card>

        {/* Modal de Edição de WhatsApp */}
        <Dialog open={!!editingWhatsapp} onOpenChange={(open) => !open && setEditingWhatsapp(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-teal-600" />
                Editar WhatsApp
              </DialogTitle>
              <DialogDescription>
                {editingWhatsapp?.nome} - {editingWhatsapp?.farmacia_nome}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="whatsapp-edit">Número do WhatsApp</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-600 bg-slate-100 px-3 py-2 rounded-md">+55</span>
                  <Input
                    id="whatsapp-edit"
                    type="text"
                    placeholder="(XX) XXXXX-XXXX"
                    value={whatsappValue}
                    onChange={(e) => setWhatsappValue(formatWhatsappInput(e.target.value))}
                    maxLength={15}
                    className="font-mono"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Digite apenas DDD + número (ex: 11 99999-9999)
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingWhatsapp(null)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveWhatsapp} 
                disabled={savingWhatsapp}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {savingWhatsapp ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SuperAdmin;
